import React, { useState, useEffect } from 'react';
import { reviewsService } from '../services/reviewsService';

export default function ReviewList({ userId, listingId = null }) {
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState({ average: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadReviews();
  }, [userId, listingId]);

  const loadReviews = async () => {
    if (!userId) {
      console.log('No userId provided to loadReviews');
      return;
    }
    
    console.log('Loading reviews for userId:', userId, 'listingId:', listingId);
    setLoading(true);
    setError('');
    
    try {
      // Load reviews for the user
      console.log('Calling reviewsService.getReviewsForUser...');
      const reviewsData = await reviewsService.getReviewsForUser(userId);
      console.log('Got reviews data:', reviewsData);
      
      // Filter by listing if specified
      const filteredReviews = listingId 
        ? reviewsData.filter(review => review.listing_id === listingId)
        : reviewsData;
      
      console.log('Filtered reviews:', filteredReviews);
      setReviews(filteredReviews);

      // Load average rating for the user
      console.log('Loading average rating...');
      const ratingData = await reviewsService.getAverageRating(userId);
      console.log('Got rating data:', ratingData);
      setAverageRating(ratingData);
    } catch (err) {
      console.error('Error loading reviews:', err);
      setError(`Failed to load reviews: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'text-yellow-400' : 'text-slate-300'
            }`}
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-4 bg-slate-200 rounded w-1/3 mb-2"></div>
          <div className="h-20 bg-slate-200 rounded"></div>
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-slate-200 rounded w-1/3 mb-2"></div>
          <div className="h-16 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
        <p className="text-red-700 text-sm">{error}</p>
        <button 
          onClick={loadReviews}
          className="mt-2 text-red-600 hover:text-red-800 font-medium text-sm"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Average Rating Summary */}
      {averageRating.count > 0 && (
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="text-2xl font-bold text-slate-900">
              {averageRating.average}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {renderStars(Math.round(averageRating.average))}
                <span className="text-sm text-slate-600">
                  ({averageRating.count} review{averageRating.count !== 1 ? 's' : ''})
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-2">⭐</div>
          <p className="text-slate-500">
            {listingId ? 'No reviews for this listing yet.' : 'No reviews yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/40">
              <div className="flex items-start gap-3">
                {/* Reviewer Avatar */}
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                  {review.reviewer_profile?.name 
                    ? review.reviewer_profile.name.charAt(0).toUpperCase() 
                    : '?'}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-slate-900">
                      {review.reviewer_profile?.name || 'Anonymous'}
                    </span>
                    <span className="text-slate-400">•</span>
                    <span className="text-sm text-slate-500">
                      {formatDate(review.created_at)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2">
                    {renderStars(review.rating)}
                  </div>
                  
                  {review.text && (
                    <p className="text-slate-700 text-sm leading-relaxed">
                      {review.text}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
